import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppInputNumber,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppTextArea,
} from '@/layout/AppComponents';
import styled from 'styled-components';
import ModuleComsaSideBarReports from './ModuleComsaSideBarReports';
import Translator from '../../../layout/components/helpers/Translator';
import { toFormData } from '../../../minions/forms';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import { handleMinDate } from '@/layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools';

const ModuleComsaLandscapeRestorationScreen = () => {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState({
    id: null,
    updateDate: null,
    restoreSurfaceTotal: 0,
    restoreSurfaceTopsoil: 0,
    restoreSurfaceHydroseeding: 0,
    restoreSurfaceBushMass: 0,
    plantationReal: 0,
    plantationProject: 0,
    occupiedSurface: 0,
    occupiedSurfaceProject: 0,
    wildlifeCrossingsCompletedReal: 0,
    wildlifeCrossingsCompletedProject: 0,
    comment: '',
  });
  const [reportType] = useState('landscapeRestoration');
  const [moduleData, setModuleData] = useState(null);

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_comsaReports_getModuleData'),
        { moduleBridgeHex, subModuleBridgeHex, plantHex },
      );
      return response.data;
    } catch (err) {}
  }, []);

  useEffect(() => {
    loadModuleData().then((res) => {
      if (isMounted.current) setModuleData(res);
    });
  }, []);
  // > LOAD MODULE DATA //

  // > LOAD REPORT //
  const loadReport = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_report'), {
        type: reportType,
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current && r.data.id) {
          setReportFormInitialValues(r.data);
        }
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (loading) {
      loadReport();
    }
  }, [loading]);
  // < LOAD REPORT //

  // < EDIT REPORT
  const editReport = async (values) => {
    setIsSendingReport(true);
    await axios
      .post(
        axiosPath('api_r_user_module_comsaReports_editReport'),
        toFormData({
          ...values,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => {
        if (isMounted.current) {
          AppNotificationAlert({
            title: r.data.landscapeRestoration.title,
            message: r.data.landscapeRestoration.message,
            type: 'success',
            position: 'top-right',
            duration: 10000,
          });
          setReportFormInitialValues(r.data.landscapeRestoration);
        }
      })
      .finally(() => {
        if (isMounted.current) setIsSendingReport(false);
      });
  };
  // < EDIT REPORT

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    restoreSurfaceTotal: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    restoreSurfaceTopsoil: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    restoreSurfaceHydroseeding: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    restoreSurfaceBushMass: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    plantationProject: Yup.number().nullable(),
    plantationReal: Yup.number().nullable(),
    occupiedSurface: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
  });
  // < FORM SCHEMA

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  ),
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaReports"
                      name="landscape.restoration"
                    />,
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_landscape_restoration"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator file="module-comsaReports" name="landscape.restoration" />
            </Title>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <Formik
                    validateOnChange={false}
                    validateOnBlur={false}
                    enableReinitialize
                    initialValues={reportFormInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={editReport}
                  >
                    {({ setFieldValue, handleSubmit, values, errors }) => (
                      <>
                        <div className="row">
                          <div className="col-md-6 offset-md-6 col-xl-3 offset-xl-9 text-center">
                            <div className="mb-3">
                              <AppDatePicker
                                title={
                                  <Translator
                                    file="module-comsaReports"
                                    name="update.date"
                                  />
                                }
                                value={values.updateDate}
                                onChange={(opt) => setFieldValue('updateDate', opt)}
                                minDate={handleMinDate(moduleData)}
                                isClearable
                              />
                              {errors.updateDate && (
                                <div className="text-danger">
                                  *{errors.updateDate}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-2 offset-md-2 text-center">
                            <label>
                              <Translator file="module-comsaReports" name="total" />
                            </label>
                          </div>
                          <div className="col-md-2 text-center">
                            <label>
                              <Translator file="module-comsaReports" name="topsoil" />
                            </label>
                          </div>
                          <div className="col-md-2 text-center">
                            <label>
                              <Translator
                                file="module-comsaReports"
                                name="hydroseeding"
                              />
                            </label>
                          </div>
                          <div className="col-md-2 text-center">
                            <label>
                              <Translator
                                file="module-comsaReports"
                                name="bush.mass"
                              />
                            </label>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-2 text-center mt-3">
                            <h6>
                              <Translator
                                file="module-comsaReports"
                                name="restore.surface"
                              />
                            </h6>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.restoreSurfaceTotal}
                                onChange={(value) =>
                                  setFieldValue('restoreSurfaceTotal', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                              />
                              {errors.restoreSurfaceTotal && (
                                <div className="text-danger">
                                  *{errors.restoreSurfaceTotal}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.restoreSurfaceTopsoil}
                                onChange={(value) =>
                                  setFieldValue('restoreSurfaceTopsoil', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">m2</span>
                                  </div>
                                }
                              />
                              {errors.restoreSurfaceTopsoil && (
                                <div className="text-danger">
                                  *{errors.restoreSurfaceTopsoil}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.restoreSurfaceHydroseeding}
                                onChange={(value) =>
                                  setFieldValue('restoreSurfaceHydroseeding', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">m2</span>
                                  </div>
                                }
                              />
                              {errors.restoreSurfaceHydroseeding && (
                                <div className="text-danger">
                                  *{errors.restoreSurfaceHydroseeding}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.restoreSurfaceBushMass}
                                onChange={(value) =>
                                  setFieldValue('restoreSurfaceBushMass', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">m2</span>
                                  </div>
                                }
                              />
                              {errors.restoreSurfaceBushMass && (
                                <div className="text-danger">
                                  *{errors.restoreSurfaceBushMass}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-2 offset-md-2 text-center">
                            <label>
                              <Translator file="module-comsaReports" name="real" />
                            </label>
                          </div>
                          <div className="col-md-2 text-center">
                            <label>
                              <Translator
                                file="module-comsaReports"
                                name="module-comsaReports.project"
                              />
                            </label>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-2 text-center mt-3">
                            <h6>
                              <Translator
                                file="module-comsaReports"
                                name="module-comsaReports.total.area.restored"
                              />
                            </h6>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.totalRestoredSurfaceReal}
                                onChange={(value) =>
                                  setFieldValue('totalRestoredSurfaceReal', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">m2</span>
                                  </div>
                                }
                              />
                              {errors.totalRestoredSurfaceReal && (
                                <div className="text-danger">
                                  *{errors.totalRestoredSurfaceReal}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.totalRestoredSurfaceProject}
                                onChange={(value) =>
                                  setFieldValue('totalRestoredSurfaceProject', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">m2</span>
                                  </div>
                                }
                              />
                              {errors.totalRestoredSurfaceProject && (
                                <div className="text-danger">
                                  *{errors.totalRestoredSurfaceProject}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-2 text-center mt-3">
                            <h6>
                              <Translator
                                file="module-comsaReports"
                                name="plantations.trees.and.bush"
                              />
                            </h6>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.plantationReal}
                                onChange={(value) =>
                                  setFieldValue('plantationReal', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">Ud.</span>
                                  </div>
                                }
                              />
                              {errors.plantationReal && (
                                <div className="text-danger">
                                  *{errors.plantationReal}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.plantationProject}
                                onChange={(value) =>
                                  setFieldValue('plantationProject', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">Ud.</span>
                                  </div>
                                }
                              />
                              {errors.plantationProject && (
                                <div className="text-danger">
                                  *{errors.plantationProject}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-2 text-center mt-2">
                            <h6>
                              <Translator
                                file="module-comsaReports"
                                name="occupied.surface.protected.natural.spaces"
                              />
                            </h6>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.occupiedSurface}
                                onChange={(value) =>
                                  setFieldValue('occupiedSurface', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">m2</span>
                                  </div>
                                }
                              />
                              {errors.occupiedSurface && (
                                <div className="text-danger">
                                  *{errors.occupiedSurface}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.occupiedSurfaceProject}
                                onChange={(value) =>
                                  setFieldValue('occupiedSurfaceProject', value)
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">m2</span>
                                  </div>
                                }
                              />
                              {errors.occupiedSurfaceProject && (
                                <div className="text-danger">
                                  *{errors.occupiedSurfaceProject}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-2 text-center mt-3">
                            <h6>
                              <Translator
                                file="module-comsaReports"
                                name="module-comsaReports.wildlife.crossings.completed"
                              />
                            </h6>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.wildlifeCrossingsCompletedReal}
                                onChange={(value) =>
                                  setFieldValue(
                                    'wildlifeCrossingsCompletedReal',
                                    value,
                                  )
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">Ud.</span>
                                  </div>
                                }
                              />
                              {errors.wildlifeCrossingsCompletedReal && (
                                <div className="text-danger">
                                  *{errors.wildlifeCrossingsCompletedReal}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="mb-3">
                              <AppInputNumber
                                value={values.wildlifeCrossingsCompletedProject}
                                onChange={(value) =>
                                  setFieldValue(
                                    'wildlifeCrossingsCompletedProject',
                                    value,
                                  )
                                }
                                icon={<AppRemixIcon icon="hashtag" noVariants />}
                                isClearable
                                append={
                                  <div className="input-group-append">
                                    <span className="input-group-text">Ud.</span>
                                  </div>
                                }
                              />
                              {errors.wildlifeCrossingsCompletedProject && (
                                <div className="text-danger">
                                  *{errors.wildlifeCrossingsCompletedProject}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            <AppTextArea
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-comsaReports"
                                  name="m-cr-comment"
                                />,
                              )}
                              value={values?.comment}
                              onChange={(v) => setFieldValue(`comment`, v)}
                              height="89px"
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            <button
                              className="btn btn-success float-right"
                              type="submit"
                              onClick={handleSubmit}
                              disabled={isSendingReport}
                            >
                              <Translator file="module-comsaReports" name="edit" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </Formik>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModuleComsaLandscapeRestorationScreen;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
